"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Button = void 0;
var _react = _interopRequireWildcard(require("react"));
var _useButtonBorderRadius = require("@cbhq/cds-common/hooks/useButtonBorderRadius");
var _useButtonIconSize = require("@cbhq/cds-common/hooks/useButtonIconSize");
var _useButtonSpacing = require("@cbhq/cds-common/hooks/useButtonSpacing");
var _useButtonVariant2 = require("@cbhq/cds-common/hooks/useButtonVariant");
var _useInteractableHeight = require("@cbhq/cds-common/hooks/useInteractableHeight");
var _useButtonSpacing2 = require("../hooks/useButtonSpacing");
var _useFlushStyles = require("../hooks/useFlushStyles");
var _Icon = require("../icons/Icon");
var _MaterialSpinner = require("../loaders/MaterialSpinner");
var foregroundColors = _interopRequireWildcard(require("../styles/foregroundColor"));
var _Pressable = require("../system/Pressable");
var _TextHeadline = require("../typography/TextHeadline");
var _linaria = require("../utils/linaria");
var buttonStyles = _interopRequireWildcard(require("./buttonStyles"));
const _excluded = ["accessibilityLabel", "block", "children", "compact", "disabled", "endIcon", "loading", "onPress", "startIcon", "to", "transparent", "flush", "type", "variant", "noScaleOnPress", "numberOfLines"];
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
const BaseButton = /*#__PURE__*/(0, _react.forwardRef)(function Button(_ref, ref) {
  let accessibilityLabel = _ref.accessibilityLabel,
    block = _ref.block,
    children = _ref.children,
    compact = _ref.compact,
    disabled = _ref.disabled,
    endIcon = _ref.endIcon,
    loading = _ref.loading,
    onPress = _ref.onPress,
    startIcon = _ref.startIcon,
    to = _ref.to,
    transparent = _ref.transparent,
    flush = _ref.flush,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'button' : _ref$type,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'primary' : _ref$variant,
    noScaleOnPress = _ref.noScaleOnPress,
    numberOfLines = _ref.numberOfLines,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const hasIcon = Boolean(startIcon !== null && startIcon !== void 0 ? startIcon : endIcon);
  const iconSize = (0, _useButtonIconSize.useButtonIconSize)(compact);
  const spacing = (0, _useButtonSpacing.useButtonSpacing)({
    flush,
    compact,
    startIcon,
    endIcon
  });
  const flushStyles = (0, _useFlushStyles.useFlushStyles)({
    flush,
    spacing
  });
  const spacingClass = (0, _useButtonSpacing2.useButtonSpacing)({
    flush,
    compact,
    startIcon,
    endIcon
  });
  const height = (0, _useInteractableHeight.useInteractableHeight)(compact);
  const borderRadius = (0, _useButtonBorderRadius.useButtonBorderRadius)(compact);
  const _useButtonVariant = (0, _useButtonVariant2.useButtonVariant)(variant, transparent),
    color = _useButtonVariant.color,
    backgroundColor = _useButtonVariant.backgroundColor,
    borderColor = _useButtonVariant.borderColor;
  const style = (0, _react.useMemo)(() => Object.assign({
    '--interactable-height': `${height}px`
  }, flushStyles), [height, flushStyles]);
  return /*#__PURE__*/_react.default.createElement(_Pressable.Pressable, _extends({
    "aria-label": accessibilityLabel !== null && accessibilityLabel !== void 0 ? accessibilityLabel : loading ? 'Loading' : undefined
  }, props, {
    ref: ref,
    background: backgroundColor,
    block: block,
    borderColor: borderColor,
    borderRadius: borderRadius,
    borderWidth: "button",
    className: (0, _linaria.cx)(foregroundColors[color], buttonStyles.button, numberOfLines && buttonStyles.unsetNoWrap, hasIcon && buttonStyles.buttonWithIcon, compact && buttonStyles.buttonCompact, block && buttonStyles.buttonBlock, spacingClass),
    disabled: disabled,
    loading: loading,
    noScaleOnPress: noScaleOnPress,
    onPress: onPress,
    style: style,
    to: to,
    transparentWhileInactive: transparent,
    type: type
  }), startIcon && /*#__PURE__*/_react.default.createElement("span", {
    className: buttonStyles.startIcon
  }, /*#__PURE__*/_react.default.createElement(_Icon.Icon, {
    color: color,
    name: startIcon,
    size: iconSize
  })), /*#__PURE__*/_react.default.createElement("span", {
    className: buttonStyles.positionRelative
  }, loading && /*#__PURE__*/_react.default.createElement("span", {
    className: (0, _linaria.cx)(buttonStyles.centerLoader)
  }, /*#__PURE__*/_react.default.createElement(_MaterialSpinner.MaterialSpinner, {
    color: color,
    size: buttonStyles.LOADERSIZE
  })), /*#__PURE__*/_react.default.createElement(_TextHeadline.TextHeadline, {
    as: "span",
    color: color,
    numberOfLines: numberOfLines
  }, /*#__PURE__*/_react.default.createElement("span", {
    className: (0, _linaria.cx)(loading && buttonStyles.visibilityHidden)
  }, children))), endIcon && /*#__PURE__*/_react.default.createElement("span", {
    className: buttonStyles.endIcon
  }, /*#__PURE__*/_react.default.createElement(_Icon.Icon, {
    color: color,
    name: endIcon,
    size: iconSize
  })));
});

// We need to override the props, otherwise they are not compatible with
// TypeScript 4.7's new "nodenext" module resolution.
const Button = BaseButton;
exports.Button = Button;